import { useState, type FormEvent } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";

import { AuthShell, authButton, authInput } from "@/components/AuthShell";
import { PasswordChecklist, isStrongPassword } from "@/components/PasswordChecklist";
import { errorMessage } from "@/lib/errors";
import { loginPathFor, usePasswordToken, useResetPassword } from "@/lib/password";

/**
 * Criar a senha a partir do link do e-mail — "esqueci minha senha" ou o
 * convite de quem entrou na equipe ou virou parceiro.
 *
 * O link é conferido antes de a pessoa digitar: descobrir que ele expirou só
 * depois de inventar uma senha forte seria frustrante à toa.
 */
export const Route = createFileRoute("/redefinir-senha")({
  head: () => ({ meta: [{ title: "Criar senha — MonteBox" }] }),
  validateSearch: (search: Record<string, unknown>): { token?: string } =>
    typeof search["token"] === "string" ? { token: search["token"] } : {},
  component: ResetPassword,
});

function ResetPassword() {
  const { token = "" } = Route.useSearch();
  const navigate = useNavigate();
  const info = usePasswordToken(token);
  const reset = useResetPassword();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [visible, setVisible] = useState(false);

  if (!token || info.isError) {
    return (
      <AuthShell
        title="Link inválido"
        subtitle="Este link expirou ou já foi usado. Peça um novo — ele chega no seu e-mail em instantes."
      >
        <Link to="/esqueci-senha" className={authButton}>
          Pedir um link novo
        </Link>
      </AuthShell>
    );
  }

  if (!info.data) {
    return (
      <AuthShell title="Conferindo o link…">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-mb-muted" />
      </AuthShell>
    );
  }

  const data = info.data;
  const invite = data.purpose === "INVITE";
  const strong = isStrongPassword(password, { name: data.name, email: data.email });
  const matches = confirm.length > 0 && confirm === password;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    reset.mutate(
      { token, password },
      {
        onSuccess: ({ role }) => {
          toast.success(invite ? "Senha criada. Agora é só entrar." : "Senha alterada. Entre com a senha nova.");
          void navigate({ to: loginPathFor(role) });
        },
      },
    );
  };

  return (
    <AuthShell
      title={invite ? "Crie sua senha" : "Crie uma senha nova"}
      subtitle={
        <>
          Olá, {data.name.split(" ")[0]}! {invite ? "Seu acesso está pronto." : ""} A senha vale
          para <strong className="text-mb-ink">{data.email}</strong>.
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <label className="block text-sm font-bold text-mb-ink">
          Senha
          <span className="relative block">
            <input
              type={visible ? "text" : "password"}
              required
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={`${authInput} pr-11`}
            />
            <button
              type="button"
              onClick={() => setVisible((value) => !value)}
              aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
              className="absolute right-3 top-1/2 mt-[3px] -translate-y-1/2 text-mb-muted hover:text-mb-navy"
            >
              {visible ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
            </button>
          </span>
        </label>
        <PasswordChecklist
          password={password}
          name={data.name}
          email={data.email}
          className="-mt-1 text-mb-muted"
        />
        <label className="block text-sm font-bold text-mb-ink">
          Repita a senha
          <input
            type={visible ? "text" : "password"}
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            aria-invalid={confirm.length > 0 && !matches}
            className={`${authInput} aria-[invalid=true]:border-destructive`}
          />
          {confirm.length > 0 && !matches && (
            <span className="mt-1 block text-xs font-medium text-destructive">
              As duas senhas não estão iguais.
            </span>
          )}
        </label>
        {reset.isError && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(reset.error)}
          </p>
        )}
        <button
          type="submit"
          disabled={reset.isPending || !strong || !matches}
          className={authButton}
        >
          {reset.isPending && <Loader2 className="h-5 w-5 animate-spin" />}
          {invite ? "Criar senha" : "Salvar senha nova"}
        </button>
      </form>
    </AuthShell>
  );
}
